import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Bot, User, Loader2 } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_QUESTIONS = [
  "How does preferential voting work?",
  "What's the difference between the Senate and the House?",
  "Who are the Teal independents?",
  "How do I enrol to vote?",
  "What is two-party preferred?",
  "What are the Greens' main policies?",
];

export default function ChatPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [messages, setMessages] = useState([
    { role: "assistant", text: "G'day! I'm AusVote AI — ask me anything about Australian politics, parties, elections, or voting. What would you like to know?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`chat-${Date.now()}`);
  const bottomRef = useRef(null);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (msg) => {
    const message = (msg || input).trim();
    if (!message) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, { message, session_id: sessionId });
      setMessages((prev) => [...prev, { role: "assistant", text: r.data.response }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", text: "Sorry, I had trouble connecting. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: bg }} data-testid="chat-page">
      {/* Header */}
      <div className="py-4 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            data-testid="chat-back-btn"
            className="flex items-center gap-1.5 text-sm font-bold"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Bot className="w-6 h-6" style={{ color: "#FFC82E" }} />
          <div>
            <h1 className="font-black text-white text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
              AusVote AI
            </h1>
            <p className="text-xs" style={{ color: "#94A3B8" }}>Powered by GPT-4o · Knows Australian politics</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-bold text-green-400">Online</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-start gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div
                className="w-8 h-8 border-2 border-black flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: m.role === "assistant" ? "#FFC82E" : "#2E5948" }}
              >
                {m.role === "assistant"
                  ? <Bot className="w-4 h-4 text-black" />
                  : <User className="w-4 h-4 text-white" />}
              </div>
              <div
                className={`max-w-lg border-2 border-black px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto" : ""}`}
                style={{
                  backgroundColor: m.role === "assistant" ? surface : "#2E5948",
                  color: m.role === "assistant" ? text : "#fff",
                  boxShadow: "2px 2px 0 #000",
                }}
                data-testid={`chat-message-${i}`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 border-2 border-black flex items-center justify-center" style={{ backgroundColor: "#FFC82E" }}>
                <Bot className="w-4 h-4 text-black" />
              </div>
              <div className="border-2 border-black px-4 py-3" style={{ backgroundColor: surface }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: textSec }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Quick questions */}
      {messages.length === 1 && (
        <div className="px-4 pb-2">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs font-bold mb-2" style={{ color: textSec }}>Quick questions:</p>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-xs px-3 py-1.5 border-2 border-black font-medium hover:-translate-y-0.5 transition-transform"
                  style={{ backgroundColor: isDark ? "#1E293B" : "#fff", color: text }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t-2 border-black px-4 py-3" style={{ backgroundColor: surface }}>
        <div className="max-w-3xl mx-auto flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask about Australian politics, parties, voting..."
            data-testid="chat-input"
            className="flex-1 border-2 border-black px-3 py-2 text-sm font-medium focus:outline-none"
            style={{ backgroundColor: isDark ? "#0F172A" : "#fff", color: text }}
            disabled={loading}
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            data-testid="chat-send-btn"
            className="flex items-center gap-1.5 px-4 py-2 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            style={{ backgroundColor: "#FFC82E", boxShadow: "2px 2px 0 #000" }}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
